/**
 * Monster Helper Functions
 *
 * Provides utility functions for monster-related logic, such as getting monster details,
 * calculating possible moves, and managing deduction states.
 *
 * Integration:
 * - Used by: useMonsters hook, Board components, and MonsterDeduction component.
 * - Depends on: monster.types.ts, board.types.ts, constants.ts.
 */

import type { MonsterTypeId, MovementTuple } from '../types/monster.types';
import type { Position, PlayerLetter } from '../types/game.types';
import { MONSTER_DEFINITIONS, BOARD_ROWS, BOARD_COLS } from './constants.ts';

// ============================================================================
// Monster Definition Helpers
// ============================================================================

/**
 * Get monster definition by ID
 * @param id - Monster type ID (1-10)
 * @returns Monster definition or null if invalid
 */
export const getMonsterDefinition = (id: MonsterTypeId): typeof MONSTER_DEFINITIONS[MonsterTypeId] | null => {
    if (id < 1 || id > 10) {
        return null;
    }
    return MONSTER_DEFINITIONS[id] || null;
};

/**
 * Get monster name by ID
 * @param id - Monster type ID (1-10)
 * @returns Monster name or null if invalid
 */
export const getMonsterName = (id: MonsterTypeId): string | null => {
    const monster = getMonsterDefinition(id);
    return monster ? monster.name : null;
};

/**
 * Get monster movement pattern by ID
 * @param id - Monster type ID (1-10)
 * @returns Array of movement tuples or empty array if invalid
 */
export const getMonsterMovement = (id: MonsterTypeId): MovementTuple[] => {
    const monster = getMonsterDefinition(id);
    return monster ? monster.movement : [];
};

// ============================================================================
// Movement Calculation Helpers
// ============================================================================

/**
 * Calculate possible moves for a monster at a given position
 * @param monsterType - Monster type ID (1-10)
 * @param position - Current position {row, col}
 * @param playerLetter - Player letter ('A' or 'B')
 * @param boardRows - Board height (default: 20)
 * @param boardCols - Board width (default: 10)
 * @returns Array of possible positions
 */
export const calculatePossibleMoves = (
    monsterType: MonsterTypeId,
    position: Position,
    playerLetter: PlayerLetter,
    boardRows: number = BOARD_ROWS,
    boardCols: number = BOARD_COLS
): Position[] => {
    const monster = getMonsterDefinition(monsterType);
    if (!monster) {
        return [];
    }

    const playerDirection = playerLetter === 'B' ? 1 : -1;
    const possibleMoves: Position[] = [];

    for (const [deltaX, deltaY] of monster.movement) {
        // Apply player direction (Y-axis is inverted)
        // Player A moves "up" (negative Y), Player B moves "down" (positive Y)
        const adjustedDeltaY = deltaY * playerDirection * -1;
        const adjustedDeltaX = deltaX * playerDirection;

        const newRow = position.row + adjustedDeltaY;
        const newCol = position.col + adjustedDeltaX;

        // Check bounds
        if (newRow >= 0 && newRow < boardRows && newCol >= 0 && newCol < boardCols) {
            possibleMoves.push({ row: newRow, col: newCol });
        }
    }

    return possibleMoves;
};

/**
 * Check if a move is valid for a given monster type
 * @param monsterType - Monster type ID (1-10)
 * @param from - Starting position
 * @param to - Target position
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns True if move is valid
 */
export const isValidMonsterMove = (
    monsterType: MonsterTypeId,
    from: Position,
    to: Position,
    playerLetter: PlayerLetter
): boolean => {
    const monster = getMonsterDefinition(monsterType);
    if (!monster) {
        return false;
    }

    const playerDirection = playerLetter === 'B' ? 1 : -1;

    const deltaRow = to.row - from.row;
    const deltaCol = to.col - from.col;

    // Same transform as calculatePossibleMoves: pattern → board delta
    return monster.movement.some(([deltaX, deltaY]) => {
        const adjustedDeltaY = deltaY * playerDirection * -1;
        const adjustedDeltaX = deltaX * playerDirection;
        return adjustedDeltaX === deltaCol && adjustedDeltaY === deltaRow;
    });
};

/**
 * Legacy getPiece function - creates a piece-like object with possible moves
 * This is for backward compatibility with existing code
 * @param row - Row position
 * @param col - Column position
 * @param monsterTypeId - Monster type ID
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns Object with getPossibleMoves method
 */
export const getPiece = (
    row: number,
    col: number,
    monsterTypeId: MonsterTypeId,
    playerLetter: PlayerLetter
): { getPossibleMoves: () => Position[] } => {
    const position: Position = { row, col };

    return {
        getPossibleMoves: () => {
            return calculatePossibleMoves(monsterTypeId, position, playerLetter);
        },
    };
};

// ============================================================================
// Monster Selection Helpers
// ============================================================================

/**
 * Check if a monster ID is valid
 * @param id - Monster ID to check
 * @returns True if valid (1-10)
 */
export const isValidMonsterId = (id: number): id is MonsterTypeId => {
    return id >= 1 && id <= 10;
};

/**
 * Get all monster IDs
 * @returns Array of all valid monster IDs (1-10)
 */
export const getAllMonsterIds = (): MonsterTypeId[] => {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as MonsterTypeId[];
};
